import React, { forwardRef } from 'react';
import { format } from 'date-fns';

// Payment status configuration
const statusConfig = {
    paid: {
        label: 'PAID',
        color: '#16a34a', // green-600
        bgColor: 'rgba(22, 163, 74, 0.08)',
        showWatermark: false,
        badgeBg: '#dcfce7', // green-100
        badgeText: '#15803d' // green-700
    },
    partial: {
        label: 'PARTIAL',
        color: '#d97706', // amber-600
        bgColor: 'rgba(217, 119, 6, 0.12)',
        showWatermark: true,
        badgeBg: '#fef3c7', // amber-100
        badgeText: '#b45309' // amber-700
    },
    unpaid: {
        label: 'UNPAID',
        color: '#dc2626', // red-600
        bgColor: 'rgba(220, 38, 38, 0.15)',
        showWatermark: true,
        badgeBg: '#fee2e2', // red-100
        badgeText: '#b91c1c' // red-700
    },
    draft: {
        label: 'DRAFT',
        color: '#6366f1', // indigo-500
        bgColor: 'rgba(99, 102, 241, 0.12)',
        showWatermark: true,
        badgeBg: '#e0e7ff', // indigo-100
        badgeText: '#4338ca' // indigo-700
    }
};

const Receipt = forwardRef(({ sale, businessDetails, paymentStatus = 'paid' }, ref) => {
    if (!sale) return null;

    const { Customer, SaleItems, total, tax, discount, createdAt, order_number } = sale;
    const { name, address, contact, tax_rate, currency, logo_url, receipt_notes } = businessDetails || {};

    // Currency symbol/code
    const currencyCode = currency || 'GHS';

    // Calculate subtotal from total, tax, discount
    const subtotal = parseFloat(total || 0) - parseFloat(tax || 0) + parseFloat(discount || 0);

    // Get status configuration
    const status = statusConfig[paymentStatus] || statusConfig.paid;

    return (
        <div ref={ref} style={{
            padding: '40px',
            backgroundColor: 'white',
            color: 'black',
            fontSize: '14px',
            fontFamily: 'Inter, Arial, sans-serif',
            maxWidth: '800px',
            margin: '0 auto',
            position: 'relative'
        }}>
            {/* Status Watermark */}
            {status.showWatermark && (
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%) rotate(-35deg)',
                    fontSize: '80px',
                    fontWeight: '900',
                    color: status.bgColor,
                    textTransform: 'uppercase',
                    letterSpacing: '10px',
                    pointerEvents: 'none',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                    zIndex: 10
                }}>
                    {status.label}
                </div>
            )}
            {/* Top Section: Company Info (Left) & Logo/Actions (Right) */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '40px' }}>
                {/* Company Info */}
                <div>
                    <h1 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px', margin: 0, color: '#111827' }}>
                        {name || "Your Company Inc."}
                    </h1>
                    <div style={{ color: '#374151', fontSize: '14px', lineHeight: '1.5' }}>
                        <p style={{ margin: 0 }}>{address?.street || "1234 Company St,"}</p>
                        <p style={{ margin: 0 }}>{address?.city}{address?.state ? `, ${address.state}` : ''} {address?.zip}</p>
                        {contact?.phone && <p style={{ margin: 0 }}>{contact.phone}</p>}
                    </div>
                </div>

                {/* Logo Area */}
                <div style={{ textAlign: 'right' }}>
                    {logo_url ? (
                        <img src={logo_url} alt="Logo" style={{ height: '60px', width: 'auto' }} />
                    ) : (
                        <div style={{
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            padding: '12px 20px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            color: '#1E3A5F',
                            backgroundColor: '#fff'
                        }}>
                            {/* Cloud upload icon can go here if needed, keeping simple text for print */}
                            <span style={{ fontWeight: '500' }}>☁ Upload Logo</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Title & Receipt Details Section */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '40px' }}>
                {/* Billed To (Left) */}
                <div style={{ width: '50%' }}>
                    <h3 style={{
                        color: '#1E3A5F',
                        fontSize: '14px',
                        fontWeight: '700',
                        marginBottom: '8px',
                        margin: 0
                    }}>Billed To</h3>
                    <div style={{ fontSize: '16px', color: '#111827' }}>
                        <p style={{ fontWeight: '500', margin: '0 0 4px 0' }}>{Customer?.name || sale.billing_fname || "Customer Name"}</p>
                        <div style={{ color: '#374151', fontSize: '14px' }}>
                            {/* Using some dummy address format if not present to match the look, or real data */}
                            {Customer?.address ? <p style={{ margin: '0' }}>{Customer.address}</p> : <p style={{ margin: '0' }}>1234 Customer St,<br />Customer Town, ST 12345</p>}
                        </div>
                    </div>
                </div>

                {/* Title & Details (Right) */}
                <div style={{ textAlign: 'right', width: '50%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px', marginBottom: '32px' }}>
                        <h2 style={{
                            fontSize: '32px',
                            fontWeight: '700',
                            color: '#003366',
                            textTransform: 'uppercase',
                            letterSpacing: '2px',
                            margin: '0'
                        }}>Sales Receipt</h2>
                    </div>

                    {/* Receipt Details Grid */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '240px' }}>
                            <span style={{ fontWeight: '700', color: '#003366' }}>Receipt #</span>
                            <span style={{ fontFamily: 'monospace', fontWeight: '500' }}>{order_number || "0000457"}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '240px' }}>
                            <span style={{ fontWeight: '700', color: '#003366' }}>Receipt date</span>
                            <span style={{ fontWeight: '500' }}>{createdAt ? format(new Date(createdAt), 'dd-MM-yyyy') : format(new Date(), 'dd-MM-yyyy')}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '240px' }}>
                            <span style={{ fontWeight: '700', color: '#003366' }}>Status</span>
                            <span style={{ fontWeight: '600', color: status.color }}>{status.label}</span>
                        </div>
                        {sale.method && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '240px' }}>
                                <span style={{ fontWeight: '700', color: '#003366' }}>Payment Method</span>
                                <span style={{ fontWeight: '500' }}>{sale.method}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Items Table */}
            <table style={{ width: '100%', marginBottom: '24px', borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ backgroundColor: '#003366', color: 'white' }}>
                        <th style={{ padding: '12px 16px', textAlign: 'left', width: '60px', fontWeight: '700', fontSize: '14px' }}>QTY</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700', fontSize: '14px' }}>Description</th>
                        <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '700', fontSize: '14px' }}>Unit Price</th>
                        <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '700', fontSize: '14px' }}>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {SaleItems?.map((item, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '16px', textAlign: 'center', fontWeight: '500', color: '#111827' }}>{item.qty}</td>
                            <td style={{ padding: '16px', color: '#111827' }}>{item.title || item.name || item.product?.title || item.product?.name || 'Unknown Product'}</td>
                            <td style={{ padding: '16px', textAlign: 'right', color: '#111827' }}>{parseFloat(item.product_price).toFixed(2)}</td>
                            <td style={{ padding: '16px', textAlign: 'right', color: '#111827' }}>{currencyCode} {parseFloat(item.total).toFixed(2)}</td>
                        </tr>
                    ))}
                    {/* Filling with empty rows if needed to match height, but for dynamic content standard map is fine */}
                </tbody>
            </table>

            {/* Divider Line */}
            <div style={{ borderTop: '2px solid #003366', marginTop: '0', marginBottom: '16px' }}></div>

            {/* Totals Section */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '60px' }}>
                <div style={{ width: '40%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '15px' }}>
                        <span style={{ color: '#111827' }}>Subtotal</span>
                        <span style={{ color: '#111827', fontWeight: '500' }}>{currencyCode} {subtotal.toFixed(2)}</span>
                    </div>
                    {parseFloat(sale.tax || 0) > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '15px' }}>
                            <span style={{ color: '#111827' }}>Sales Tax ({tax_rate || 0}%)</span>
                            <span style={{ color: '#111827', fontWeight: '500' }}>{currencyCode} {parseFloat(sale.tax || 0).toFixed(2)}</span>
                        </div>
                    )}
                    {parseFloat(sale.discount || 0) > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '15px', color: '#DC2626' }}>
                            <span>Discount</span>
                            <span>- {currencyCode} {parseFloat(sale.discount).toFixed(2)}</span>
                        </div>
                    )}

                    <div style={{ borderTop: '1px solid #E5E7EB', margin: '8px 0' }}></div>

                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        color: '#003366',
                        fontSize: '18px',
                        fontWeight: '800'
                    }}>
                        <span>Total ({currencyCode})</span>
                        <span>{currencyCode} {parseFloat(sale.total).toFixed(2)}</span>
                    </div>
                    <div style={{ borderTop: '2px solid #003366', marginTop: '12px' }}></div>
                </div>
            </div>

            {/* Footer / Notes */}
            <div>
                <h4 style={{ fontWeight: '700', color: '#003366', marginBottom: '8px', margin: 0, fontSize: '14px' }}>Notes</h4>
                <p style={{ color: '#374151', margin: '4px 0', fontSize: '14px' }}>{receipt_notes || "Thank you for your business!"}</p>
            </div>
        </div>
    );
});

Receipt.displayName = "Receipt";
export default Receipt;
